import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { SuggestionCard } from '@/components/template-ideas/SuggestionCard';
import {
  SuggestTemplateDialog, type SuggestTemplateInput,
} from '@/components/template-ideas/SuggestTemplateDialog';
import { rankSuggestions } from '@/lib/template-suggestions/rankSuggestions';
import type {
  MyTemplateSuggestion, TemplateSuggestion,
} from '@/lib/template-suggestions/types';
import {
  CREATE_MY_TEMPLATE_SUGGESTION,
  MY_TEMPLATE_SUGGESTIONS,
  TEMPLATE_SUGGESTIONS,
  UNVOTE_TEMPLATE_SUGGESTION,
  VOTE_TEMPLATE_SUGGESTION,
} from '@/queries/templateSuggestions';

const KNOWN_ERRORS = ['invalid_url', 'missing_field', 'not_votable'] as const;

function messageKeyFor(error: unknown): string {
  const raw = error instanceof Error ? error.message : '';
  const match = KNOWN_ERRORS.find((code) => raw.includes(code));
  return `templateIdeas.errors.${match ?? 'generic'}`;
}

export default function TemplateIdeas() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicate, setDuplicate] = useState<TemplateSuggestion | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingVoteIds, setPendingVoteIds] = useState<Set<string>>(new Set());

  const listQuery = useQuery(TEMPLATE_SUGGESTIONS, { variables: { limit: 50, offset: 0 } });
  const mineQuery = useQuery(MY_TEMPLATE_SUGGESTIONS);

  const [createSuggestion, createState] = useMutation(CREATE_MY_TEMPLATE_SUGGESTION);
  const [vote] = useMutation(VOTE_TEMPLATE_SUGGESTION);
  const [unvote] = useMutation(UNVOTE_TEMPLATE_SUGGESTION);

  const suggestions: TemplateSuggestion[] = useMemo(
    () => listQuery.data?.templateSuggestions?.data ?? [],
    [listQuery.data],
  );
  const mine: MyTemplateSuggestion[] = useMemo(
    () => mineQuery.data?.myTemplateSuggestions ?? [],
    [mineQuery.data],
  );
  const { votable, promoted } = useMemo(() => rankSuggestions(suggestions), [suggestions]);

  const handleSubmit = useCallback(async (input: SuggestTemplateInput) => {
    setFormError(null);
    setDuplicate(null);
    try {
      const { data } = await createSuggestion({ variables: { input } });
      const result = data?.createMyTemplateSuggestion;
      if (result?.duplicate) {
        setDuplicate(result.duplicate);
        return;
      }
      // Already suggested, but the existing row is pending/rejected and this
      // viewer may not see it. Say the post is taken without naming it.
      if (result?.duplicate_hidden) {
        setFormError(t('templateIdeas.duplicate.hidden'));
        return;
      }
      setDialogOpen(false);
      toast({ description: t('templateIdeas.toast.submitted') });
      await mineQuery.refetch();
    } catch (error) {
      setFormError(t(messageKeyFor(error)));
    }
  }, [createSuggestion, mineQuery, t, toast]);

  const handleViewDuplicate = useCallback((suggestion: TemplateSuggestion) => {
    setDialogOpen(false);
    document
      .querySelector(`[data-suggestion-id="${suggestion.id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleToggleVote = useCallback(async (suggestion: TemplateSuggestion) => {
    setPendingVoteIds((prev) => new Set(prev).add(suggestion.id));
    try {
      if (suggestion.viewer_has_voted) {
        await unvote({ variables: { id: suggestion.id } });
        toast({ description: t('templateIdeas.toast.unvoted') });
      } else {
        await vote({ variables: { id: suggestion.id } });
        toast({ description: t('templateIdeas.toast.voted') });
      }
      await listQuery.refetch();
    } catch (error) {
      toast({ variant: 'destructive', description: t(messageKeyFor(error)) });
    } finally {
      setPendingVoteIds((prev) => {
        const next = new Set(prev);
        next.delete(suggestion.id);
        return next;
      });
    }
  }, [listQuery, t, toast, unvote, vote]);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-4xl space-y-8 px-4 py-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('templateIdeas.eyebrow')}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold">{t('templateIdeas.title')}</h1>
            <Button data-testid="open-suggest-dialog" onClick={() => setDialogOpen(true)}>
              {t('templateIdeas.suggest')}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{t('templateIdeas.subtitle')}</p>
        </header>

        <section className="space-y-3">
          {/* A failed query must never be mistaken for an empty ranking. */}
          {listQuery.error ? (
            <div className="space-y-2" data-testid="suggestions-error">
              <p className="text-sm text-destructive">{t('templateIdeas.loadError')}</p>
              <Button size="sm" variant="secondary" onClick={() => { listQuery.refetch(); }}>
                {t('templateIdeas.retry')}
              </Button>
            </div>
          ) : (
            votable.length === 0 && !listQuery.loading && (
              <p className="text-sm text-muted-foreground">{t('templateIdeas.empty')}</p>
            )
          )}
          {votable.map((suggestion, index) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              rank={index + 1}
              pending={pendingVoteIds.has(suggestion.id)}
              onToggleVote={handleToggleVote}
            />
          ))}
        </section>

        {promoted.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">{t('templateIdeas.promotedTitle')}</h2>
            {promoted.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                pending={pendingVoteIds.has(suggestion.id)}
                onToggleVote={handleToggleVote}
              />
            ))}
          </section>
        )}

        <section className="space-y-3" data-testid="my-suggestions">
          <h2 className="text-lg font-semibold">{t('templateIdeas.mineTitle')}</h2>
          {mineQuery.error ? (
            <div className="space-y-2" data-testid="my-suggestions-error">
              <p className="text-sm text-destructive">{t('templateIdeas.mineLoadError')}</p>
              <Button size="sm" variant="secondary" onClick={() => { mineQuery.refetch(); }}>
                {t('templateIdeas.retry')}
              </Button>
            </div>
          ) : (
            mine.length === 0 && !mineQuery.loading && (
              <p className="text-sm text-muted-foreground">{t('templateIdeas.mineEmpty')}</p>
            )
          )}
          {mine.map((suggestion) => (
            <Card key={suggestion.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-2 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium">{suggestion.title}</p>
                  <p className="truncate text-sm text-muted-foreground">{suggestion.use_case}</p>
                  {suggestion.status === 'rejected' && suggestion.review_reason && (
                    <p className="text-sm text-destructive">{suggestion.review_reason}</p>
                  )}
                </div>
                <Badge variant="outline">{t(`templateIdeas.status.${suggestion.status}`)}</Badge>
              </CardContent>
            </Card>
          ))}
        </section>
      </div>

      <SuggestTemplateDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setDuplicate(null); setFormError(null); }
        }}
        submitting={createState.loading}
        duplicate={duplicate}
        errorMessage={formError}
        onSubmit={handleSubmit}
        onViewDuplicate={handleViewDuplicate}
      />
    </Layout>
  );
}
